import React, { useMemo, useState } from 'react';
import { App, Button, Card, Space, Switch, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';

const { Title, Text } = Typography;

type Row = {
  id: string;
  grn_number: string;
  receipt_date: string;
  po_number: string;
  item_code: string;
  item_name: string;
  warehouse_name: string;
  revalue_qty: number;
  posted_rate: number;
  landed_rate: number;
  unit_delta: number;
  value_delta: number;
};

async function fetchRows(): Promise<Row[]> {
  const res = await api.get<{ rows?: Array<Record<string, unknown>> }>(
    '/inventory/reports/landed-cost-revalue',
    {
      params: { page_size: 500 },
    }
  );
  const data = res.data;
  const rows = (data?.rows ?? (Array.isArray(data) ? data : []) ?? []) as Array<
    Record<string, unknown>
  >;
  return rows.map((r) => ({
    id: String(r.id ?? ''),
    grn_number: String(r.grn_number ?? ''),
    receipt_date: String(r.receipt_date ?? ''),
    po_number: String(r.po_number ?? ''),
    item_code: String(r.item_code ?? ''),
    item_name: String(r.item_name ?? ''),
    warehouse_name: String(r.warehouse_name ?? ''),
    revalue_qty: Number(r.revalue_qty ?? 0),
    posted_rate: Number(r.posted_rate ?? 0),
    landed_rate: Number(r.landed_rate ?? 0),
    unit_delta: Number(r.unit_delta ?? 0),
    value_delta: Number(r.value_delta ?? 0),
  }));
}

export const LandedCostRevaluePage: React.FC = () => {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [positiveOnly, setPositiveOnly] = useState(false);

  const q = useQuery({ queryKey: ['landed-cost-revalue'], queryFn: fetchRows });
  const rows = useMemo(() => {
    const all = q.data ?? [];
    return positiveOnly ? all.filter((r) => r.value_delta > 0) : all;
  }, [q.data, positiveOnly]);

  const applyMut = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await api.post<{ applied?: number; value_delta_total?: number }>(
        '/inventory/landed-cost-revalue/apply',
        { grn_item_ids: ids }
      );
      return res.data ?? {};
    },
    onSuccess: (data: { applied?: number; value_delta_total?: number }) => {
      message.success(`Applied ${data.applied ?? 0} line(s); value Δ ${data.value_delta_total ?? 0}`);
      setSelected([]);
      qc.invalidateQueries({ queryKey: ['landed-cost-revalue'] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Apply failed'),
  });

  const columns: ColumnsType<Row> = [
    { title: 'GRN', dataIndex: 'grn_number', width: 120 },
    { title: 'PO', dataIndex: 'po_number', width: 120 },
    { title: 'Item', dataIndex: 'item_code', width: 120 },
    { title: 'Description', dataIndex: 'item_name', ellipsis: true },
    { title: 'Warehouse', dataIndex: 'warehouse_name', width: 140 },
    { title: 'Qty', dataIndex: 'revalue_qty', width: 90, align: 'right' },
    { title: 'Posted rate', dataIndex: 'posted_rate', width: 100, align: 'right' },
    { title: 'Landed rate', dataIndex: 'landed_rate', width: 100, align: 'right' },
    { title: 'Unit Δ', dataIndex: 'unit_delta', width: 90, align: 'right' },
    {
      title: 'Value Δ',
      dataIndex: 'value_delta',
      width: 100,
      align: 'right',
      render: (v) => <Text strong>{v}</Text>,
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 12 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Landed cost revalue
          </Title>
          <Text type="secondary">
            Posted GRNs where PO landed cost exceeds receipt rate and quantity remains on hand. Applies
            value-only stock revaluation + inventory / accrued-cost JV.
          </Text>
        </div>
        <Space>
          <span>Positive Δ only</span>
          <Switch checked={positiveOnly} onChange={setPositiveOnly} />
          <Button onClick={() => q.refetch()}>Refresh</Button>
          <Button
            type="primary"
            disabled={!selected.length}
            loading={applyMut.isPending}
            onClick={() => applyMut.mutate(selected)}
          >
            Apply selected ({selected.length})
          </Button>
          <Button
            disabled={!rows.length}
            loading={applyMut.isPending}
            onClick={() => applyMut.mutate(rows.map((r) => r.id))}
          >
            Apply all
          </Button>
        </Space>
      </Space>
      <Card>
        <Table
          rowKey="id"
          loading={q.isLoading}
          dataSource={rows}
          columns={columns}
          size="small"
          pagination={{ pageSize: 50 }}
          rowSelection={{
            selectedRowKeys: selected,
            onChange: (keys) => setSelected(keys as string[]),
          }}
          scroll={{ x: 1100 }}
        />
      </Card>
    </div>
  );
};

export default LandedCostRevaluePage;
